import { uniqBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useOpenNotification } from 'notifications/hooks';
import { useBellNotificationsContext } from 'notifications/states';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getNotificationInternalPath } from 'notifications/utils/notification';
import { planNotificationToasts } from 'notifications/utils/toast';
import { isIframePath } from 'router/utils/iframe';

import { NotificationDrawer } from '../NotificationDrawer';
import { NotificationToastStack } from '../NotificationToastStack';

// The bell's drawer and the toast stack of new arrivals, mounted once by the router.
export const NotificationHost = () => {
  const [arrivals, setArrivals] = useState<IBellNotification[]>([]);
  const plannedArrivalsRef = useRef<IBellNotification[] | null>(null);

  const { pathname } = useLocation();

  const {
    notifications,
    newArrivals,
    unreadCount,
    drawerList,
    listFilter,
    isPanelOpen,
    closePanel,
    collapsePages,
    dismiss,
    dismissAll,
    markAllRead,
    markRead,
    markUnread,
    refresh,
    setListFilter,
  } = useBellNotificationsContext();
  const { openAction, openNotification } = useOpenNotification();
  const lastPathnameRef = useRef(pathname);

  // Hiding only takes the card away: the notification stays unread in the list.
  const hideArrival = useCallback((id: number) => {
    setArrivals((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const onClick = useCallback(
    (notification: IBellNotification) => {
      hideArrival(notification.id);

      if (getNotificationInternalPath(notification)) {
        closePanel();
      }

      openNotification(notification);
    },
    [closePanel, hideArrival, openNotification]
  );

  // An external link opens a new tab, so the panel stays open for the next one.
  const onAction = useCallback(
    (notification: IBellNotification, cta: IBellNotificationCta) => {
      hideArrival(notification.id);

      if (!cta.external) {
        closePanel();
      }

      openAction(notification, cta);
    },
    [closePanel, hideArrival, openAction]
  );

  const onDismiss = useCallback(
    (id: number) => {
      hideArrival(id);
      dismiss(id);
    },
    [dismiss, hideArrival]
  );

  const onToggleRead = useCallback(
    (notification: IBellNotification) =>
      notification.isRead ? markUnread(notification.id) : markRead(notification.id),
    [markRead, markUnread]
  );

  // Once per batch: a navigation re-runs this effect with the batch it already planned.
  useEffect(() => {
    if (newArrivals === plannedArrivalsRef.current) {
      return;
    }

    plannedArrivalsRef.current = newArrivals;

    // The provider empties the batch when the user or the company changes.
    if (!newArrivals?.length) {
      setArrivals([]);

      return;
    }

    // The open panel already lists the batch; it is consumed, not shown later.
    if (isPanelOpen) {
      return;
    }

    const planned = planNotificationToasts(newArrivals, pathname);

    setArrivals((prev) => uniqBy([...planned, ...prev], 'id'));
  }, [isPanelOpen, newArrivals, pathname]);

  // react-query refetches every loaded page on each invalidation: the pages loaded with
  // "Load more" go once the panel closes, and it opens on a fresh first page.
  useEffect(() => {
    if (isPanelOpen) {
      refresh();

      return;
    }

    collapsePages();
  }, [collapsePages, isPanelOpen, refresh]);

  // The open list has every arrival in it.
  useEffect(() => {
    if (isPanelOpen) {
      setArrivals([]);
    }
  }, [isPanelOpen]);

  // A card leaves once its notification is read or dismissed anywhere (the panel, a card,
  // another tab after its poll).
  useEffect(() => {
    setArrivals((prev) => {
      const next = prev.filter((arrival) => {
        const latest = notifications?.find(({ id }) => id === arrival.id);

        return !!latest && !latest.isRead;
      });

      return next.length === prev.length ? prev : next;
    });
  }, [notifications]);

  // The panel outlives the page: any navigation closes it, as the bell's page change used to.
  useEffect(() => {
    if (pathname === lastPathnameRef.current) {
      return;
    }

    lastPathnameRef.current = pathname;
    closePanel();
  }, [closePanel, pathname]);

  if (isIframePath(pathname)) {
    return null;
  }

  return (
    <>
      <NotificationToastStack
        arrivals={arrivals}
        onClick={onClick}
        onAction={onAction}
        onHide={hideArrival}
      />

      <NotificationDrawer
        notifications={drawerList.notifications}
        listFilter={listFilter}
        unreadCount={unreadCount}
        onListFilterChange={setListFilter}
        onClick={onClick}
        onAction={onAction}
        onToggleRead={onToggleRead}
        onDismiss={onDismiss}
        onMarkAllRead={markAllRead}
        onDismissAll={dismissAll}
        onLoadMore={drawerList.loadMore}
        onClose={closePanel}
        isOpen={isPanelOpen}
        hasMore={drawerList.hasMore}
        isFetchingMore={drawerList.isFetchingMore}
        isLoading={drawerList.isLoading}
        isFetching={drawerList.isFetching}
        isError={drawerList.isError}
      />
    </>
  );
};
